/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import gql from 'graphql-tag';
import { compact, keyBy, sortBy } from 'lodash';
import { compose } from 'recompose';

import mapProps from 'js/lib/mapProps';
import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography } from '@coursera/cds-core';

import withFragments from 'bundles/graphql/components/withFragments';
import FullWidthCard from 'bundles/partners-graphql/components/FullWidthCard';
import type {
  PartnerPageQuery_PartnersV1Resource_shortName_partners_professionalCertificates as ProfessionalCertificates,
  PartnerPageQuery_PartnersV1Resource_shortName_partners_professionalCertificates_elements as ProfessionalCertificatesData,
  PartnerPageQuery_PartnersV1Resource_shortName_partners_specializations_elements as S12nData,
  PartnerPageQuery_PartnersV1Resource_shortName_partners_specializations as Specializations,
} from 'bundles/partners-graphql/components/__generated__/PartnerPageQuery';
import { getS12nVariantProCertLabel, getSdpUrl } from 'bundles/s12n-common/lib/s12nProductVariantUtils';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  container: css`
    padding: 15px;
  `,
  header: css`
    margin-bottom: 16px !important;
  `,
};

type PropsFromCaller = {
  specializations: Specializations | null;
  professionalCertificates: ProfessionalCertificates | null;
};

export type S12nDataWithLaunchedAtDefined = Omit<S12nData, 'launchedAt'> & {
  launchedAt: number;
};

type PropsToComponent = {
  specializations: S12nDataWithLaunchedAtDefined[];
  professionalCertificates: ProfessionalCertificatesData[];
};

export const SpecializationCards = ({ specializations, professionalCertificates }: PropsToComponent) => {
  const uniqueSpecialisations = specializations.filter(
    (specialization, index, array) => array.findIndex((s12n) => s12n.id === specialization.id) === index
  );

  const professionalCertsDict = keyBy(professionalCertificates, 'id');

  // We do some hacky-ish processing here. Some important notes:
  // 1. It appears that the list of s12ns is generally a superset of professional certs
  // 2. The tagline in the professional certs data is preferred over the tagline in the s12n data if it exists
  // 3. It's important to build the slugs correctly for each offering
  const processedS12ns = sortBy(uniqueSpecialisations, (s12n) => s12n.name).map((s12n) => {
    let tagline = s12n.tagline;
    const matchedProfessionalCert = professionalCertsDict[s12n.id];

    if (matchedProfessionalCert) {
      tagline = matchedProfessionalCert.tagline;
    }

    return {
      ...s12n,
      tagline,
      slug: getSdpUrl(s12n.productVariant, s12n.slug),
      name: `${s12n.name} ${getS12nVariantProCertLabel(s12n.productVariant)}`,
    };
  });

  const getAvailabilityMessage = (plannedLaunchDate: number | null) =>
    plannedLaunchDate
      ? _t('Starting #{plannedLaunchDate}', {
          plannedLaunchDate: formatDateTimeDisplay(plannedLaunchDate, MOMENT_FORMATS.LONG_DATE_ONLY_DISPLAY),
        })
      : 'Available now';

  return (
    <div css={styles.container}>
      <Typography css={styles.header} variant="h2" component="h3">
        {_t('Certificates + Specializations')}
      </Typography>
      {processedS12ns &&
        processedS12ns.map((s12n) => (
          <FullWidthCard
            key={s12n.id}
            logo={s12n.logo || ''}
            name={s12n.name}
            tagline={s12n.tagline || undefined}
            slug={s12n.slug}
            availability={getAvailabilityMessage(s12n.plannedLaunchDate)}
          />
        ))}
    </div>
  );
};

export const specializationFragment = gql`
  fragment SpecializationsFragment on OnDemandSpecializationsV1Connection {
    elements {
      id
      name
      slug
      logo
      productVariant
      launchedAt
      courseIds
      tagline
      plannedLaunchDate
    }
  }
`;

export const professionalCertificatesFragment = gql`
  fragment ProfessionalCertificates on ProfessionalCertsV1Connection {
    elements {
      id
      slug
      name
      tagline
      thumbnail
    }
  }
`;

export default compose<PropsToComponent, PropsFromCaller>(
  withFragments({
    specializations: specializationFragment,
    professionalCertificates: professionalCertificatesFragment,
  }),
  mapProps<PropsToComponent, PropsFromCaller>(({ specializations, professionalCertificates, ...rest }) => {
    return {
      professionalCertificates: compact(professionalCertificates?.elements),
      specializations: compact(specializations?.elements)
        // Only show s12ns that are launched or have a planned launch date.
        .filter((s12n): s12n is S12nDataWithLaunchedAtDefined => {
          return !!s12n.launchedAt || !!s12n.plannedLaunchDate;
        }),
      ...rest,
    };
  })
)(SpecializationCards);
